import { Link } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, ExternalLink, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import type { StaticPage } from "@shared/schema";

export function AdminPagesList() {
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["admin", "pages"],
    queryFn: () => api<StaticPage[]>("/api/admin/pages"),
  });

  const del = useMutation({
    mutationFn: (id: string) =>
      api(`/api/admin/pages/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "pages"] }),
  });

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Statikus oldalak</h1>
        <Link href="/pages/new" className="btn-primary">
          <Plus size={16} /> Új oldal
        </Link>
      </div>

      <p className="mt-2 text-sm text-brand-500">
        Adatvédelem, impresszum, stb. — markdown szerkeszthető oldalak. Public
        URL: <code>/o/&lt;slug&gt;</code>
      </p>

      <div className="mt-6 card overflow-hidden">
        {q.isLoading && (
          <div className="p-8 text-brand-500">Betöltés...</div>
        )}
        {q.data && q.data.length === 0 && (
          <div className="p-8 text-brand-500">Még nincs statikus oldal.</div>
        )}
        {q.data && q.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-brand-50 text-brand-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Cím</th>
                <th className="text-left px-4 py-3">Slug (URL)</th>
                <th className="text-left px-4 py-3">Publikálva</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((p) => (
                <tr key={p.id} className="border-t border-brand-200">
                  <td className="px-4 py-3 font-medium">
                    <Link
                      href={`/pages/${p.id}`}
                      className="hover:text-brand-700"
                    >
                      {p.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-brand-500 font-mono text-xs">
                    /o/{p.slug}
                  </td>
                  <td className="px-4 py-3">
                    {p.isPublished ? (
                      <span className="text-xs text-emerald-700">Igen</span>
                    ) : (
                      <span className="text-xs text-brand-400">Nem</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                    <Link
                      href={`/pages/${p.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white bg-accent hover:bg-accent-700 px-3 py-1.5 rounded transition-colors"
                      title="Szerkesztés"
                    >
                      <Pencil size={13} /> Szerkesztés
                    </Link>
                    <a
                      href={`/o/${p.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-500 hover:text-brand-900 inline-block align-middle"
                      title="Megnyitás a weboldalon"
                    >
                      <ExternalLink size={16} />
                    </a>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(`Biztosan törli: ${p.title}?`)) {
                          del.mutate(p.id);
                        }
                      }}
                      className="text-red-500 hover:text-red-700"
                      title="Törlés"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
